import { useContext, useState, useCallback } from 'react';
import {
  AutoraterJustificationSuggestionRequest,
  AutoraterLikertJustificationSuggestionRequest,
  TextCollectionPerResponseStepResponse,
} from '@scale/shared/types/chat';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import {
  fetchAutoraterJustificationSuggestions,
  fetchAutoraterLikertJustificationSuggestions,
} from '../helpers';
import { useResponseSuggestionsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseSuggestions/stores/useResponseSuggestionsStore';
import logger from 'react-scale/lib/logging';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';

export interface AutoraterResponse {
  justification?: string;
  likertScore?: number;
}

export interface FetchAutoraterInput {
  key: string;
  prompt: string;
  responseDetails: TextCollectionPerResponseStepResponse | null;
  likertScore?: number;
}

export interface UseResponseSuggestionsResponse {
  suggestions?: AutoraterResponse;
  justificationLoading: boolean;
  likertScoreLoading: boolean;
  fetchJustifications: (input: FetchAutoraterInput) => void;
  fetchLikertScore: (input: FetchAutoraterInput) => void;
}

const useResponseSuggestions = (): UseResponseSuggestionsResponse | null => {
  const [suggestions, setSuggestions] = useState<AutoraterResponse>({});
  const [loading, setLoading] = useState({
    justificationLoading: false,
    likertScoreLoading: false,
  });
  const { getAutoraterState, setAutoraterState } = useResponseSuggestionsStore();
  const taskContext = useContext(ChatTaskContext);
  const taskId = taskContext.taskId ?? taskContext.assignmentId;

  const fetchJustifications = useCallback(
    async ({ key, prompt, responseDetails, likertScore }: FetchAutoraterInput) => {
      if (!taskContext.projectId || !taskId || !responseDetails || !prompt) {
        setSuggestions({});
        return;
      }
      if (!likertScore) {
        return;
      }
      const justificationsKey = `${key}-${likertScore}`;
      if (getAutoraterState(justificationsKey)) {
        setSuggestions(prev => ({
          ...prev,
          justification: getAutoraterState(justificationsKey)?.justification,
        }));
      } else {
        setLoading(prev => ({ ...prev, justificationLoading: true }));
        try {
          const justificationPayload: AutoraterJustificationSuggestionRequest = {
            project_id: taskContext.projectId,
            task_id: taskId,
            prompt,
            response_details: generateResponseDetails(responseDetails),
            likert_score: likertScore,
          };
          const justificationData = await fetchAutoraterJustificationSuggestions(
            justificationPayload,
          );
          setAutoraterState(justificationsKey, { justification: justificationData.justification });
          setSuggestions(prev => ({ ...prev, justification: justificationData.justification }));
        } catch (err) {
          logger.warn(`Error fetching Archie justifications suggestions [${err}]`);
          defaultApiFetchErrorHandler(err, {
            handleHttp: true,
            message: 'Something went wrong while fetching Archie justification suggestions.',
          });
        } finally {
          setLoading(prev => ({ ...prev, justificationLoading: false }));
        }
      }
    },
    [getAutoraterState, setAutoraterState, taskContext.projectId, taskId],
  );

  const fetchLikertScore = useCallback(
    async ({ key, prompt, responseDetails }: FetchAutoraterInput) => {
      if (!taskContext.projectId || !taskId || !responseDetails || !prompt) {
        setSuggestions({});
        return;
      }
      if (getAutoraterState(key)) {
        setSuggestions(prev => ({ ...prev, likertScore: getAutoraterState(key)?.likertScore }));
      } else {
        setLoading(prev => ({ ...prev, likertScoreLoading: true }));
        try {
          const likertPayload: AutoraterLikertJustificationSuggestionRequest = {
            project_id: taskContext.projectId,
            task_id: taskId,
            prompt,
            response_details: generateResponseDetails(responseDetails),
          };
          const likertData = await fetchAutoraterLikertJustificationSuggestions(likertPayload);
          setAutoraterState(key, { likertScore: likertData.likert_score });
          setSuggestions(prev => ({ ...prev, likertScore: likertData.likert_score }));
        } catch (err) {
          logger.warn(`Error fetching Archie likert suggestions [${err}]`);
          defaultApiFetchErrorHandler(err, {
            handleHttp: true,
            message: 'Something went wrong while fetching Archie likert suggestions.',
          });
        } finally {
          setLoading(prev => ({ ...prev, likertScoreLoading: false }));
        }
      }
    },
    [getAutoraterState, setAutoraterState, taskContext.projectId, taskId],
  );

  return {
    suggestions,
    justificationLoading: loading.justificationLoading,
    likertScoreLoading: loading.likertScoreLoading,
    fetchJustifications,
    fetchLikertScore,
  };
};

function generateResponseDetails(perResponseOutput: TextCollectionPerResponseStepResponse) {
  try {
    const responseDetails: Record<string, { [key: string]: unknown }> = {};
    const annotations = perResponseOutput.context.annotations;
    const candidates = perResponseOutput.context.candidates;

    for (const modelName in annotations) {
      const modelData = annotations[modelName].annotations;
      responseDetails[modelName] = {
        [modelName]: candidates.find(candidate => candidate.id === modelName)?.message.content,
      };

      for (const key in modelData) {
        responseDetails[modelName][key] = (modelData as any)[key].response[0];
      }
    }
    return responseDetails;
  } catch (error) {
    logger.warn(`Error generating response details for Archie suggestions [${error}]`);
    return {};
  }
}

export default useResponseSuggestions;
